/**
 * Contact Enrichment Workflow
 *
 * This workflow enriches contact profiles with communication insights:
 * 1. Triggers when a contact is updated
 * 2. Gathers all historical interactions (messages, calls, emails)
 * 3. Generates an AI-powered communication profile
 * 4. Notifies user of insights discovered
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const contactEnrichmentWorkflow: Workflow = {
  id: 'contact-enrichment',
  name: 'Arricchimento Profilo Contatto',
  description: 'Analizza le interazioni con un contatto e genera un profilo comunicativo dettagliato',
  trigger: {
    type: 'event',
    config: {
      eventType: 'entity.updated',
      entityType: 'contact',
      // Only trigger if significant fields changed
      watchFields: ['email', 'phone', 'company', 'role'],
    },
  },
  steps: [
    {
      id: 'analyze_interactions',
      name: 'Analizza Interazioni',
      type: 'custom',
      config: {
        // Gather all interaction types
        includeMessages: true,
        includeCalls: true,
        includeEmails: true,
        includeEvents: true,
        // Limit to recent history
        timeRangeMonths: 12,
      },
      next: ['generate_profile'],
    },
    {
      id: 'generate_profile',
      name: 'Genera Profilo',
      type: 'ai_summarize',
      config: {
        prompt: `Create a communication profile for this contact based on their interaction history.

Analyze:
- Communication frequency and patterns
- Preferred communication channels
- Response time and availability
- Topics of interest
- Relationship strength indicators

Generate a concise profile in JSON:
{
  "profile": {
    "communicationStyle": "formal|casual|technical",
    "preferredChannels": ["email", "phone", "chat"],
    "responseTimeAvg": "hours",
    "topTopics": ["topic1", "topic2"],
    "relationshipScore": 0-100,
    "insights": ["key insight 1", "key insight 2"]
  }
}`,
        maxTokens: 500,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Notifica Aggiornamento',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Ho aggiornato il profilo di {contactName} con nuovi insights.',
        includeProfile: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate relationship strength from interaction frequency
 */
export function calculateRelationshipScore(interactionCount: number, daysSinceFirst: number): number {
  if (daysSinceFirst === 0) return 0;

  const interactionsPerMonth = (interactionCount / daysSinceFirst) * 30;
  // Score: 0-100 based on monthly interaction frequency
  return Math.min(100, Math.floor(interactionsPerMonth * 20));
}
